// src/components/goal/GoalStepper.js

import { useEffect, useState } from "react";

import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Stepper,
  Step,
  StepLabel,
  Typography,
} from '@mui/material';
import Step1 from './steps/Step1';
import Step2 from './steps/Step2';
import Step3 from './steps/Step3';
import Step4 from './steps/Step4';
import axios from "axios";
import dayjs from "dayjs";

const steps = ['Períodos', 'Metas por Período', 'Folgas', 'Valores'];

export default function GoalStepper({goalId, setPage}) {
  const [activeStep, setActiveStep] = useState(0);
  const [periodos, setPeriodos] = useState(1);
  const [metas, setMetas] = useState([{ id: 0, nome: 'Período 1', valor: 0 }]);
  const [openDialog, setOpenDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [goal, setGoal] = useState({
    company_id: JSON.parse(localStorage.getItem('user')).company_id || 0,
    name: "",
    type: "amount",
    period: dayjs().format("YYYY-MM"),
    start_date: dayjs().startOf("month").format("YYYY-MM-DD"),
    end_date: dayjs().endOf("month").format("YYYY-MM-DD"),
    target: null,
    periods: []
  });

  const [absences, setAbsences] = useState([]);
  const [sellers, setSellers] = useState([]);

  useEffect(() => {
    const fetch = async () => {
      const headers = {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      };
      try {
        const { data } = await axios.get(`${process.env.REACT_APP_API_URL}admin/goal/${goalId}`, { headers });
        setGoal(data);
      } catch (err) {
        console.error("Erro na requisição:", err);
      } finally {
      }
    };
    if (goalId) {
      fetch();
    }
  }, [goalId]);

  const handleNext = () => {
    if (activeStep === 3) {
      setOpenDialog(true);
    } else {
      // Validações específicas para a etapa 1
      if (activeStep === 0) {
        if (!goal.name || goal.name.trim() === '' || goal.name.length < 3) {
          setErrorMessage('Preencha um nome válido para a meta.');
          return;
        }
        if (!goal.period) {
          setErrorMessage('Selecione um período válido para a meta.');
          return;
        }
        if (!goal.start_date || !goal.end_date) {
          setErrorMessage('Selecione datas de início e fim válidas para a meta.');
          return;
        }
        if (dayjs(goal.end_date).isBefore(dayjs(goal.start_date))) {
          setErrorMessage("A data de término deve ser posterior à data de início.");
          return;
        }
        if (goal.target === null || goal.target <= 0) {
          setErrorMessage("Preencha um valor válido para a meta.");
          return;
        }
      }
      setErrorMessage('');
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (activeStep === 0) {
      setPage('goals');
    } else {
      setActiveStep((prev) => prev - 1);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setActiveStep((prev) => prev + 1);
  };

  const handlePeriodoChange = (val) => {
    setPeriodos(val);
    const novos = Array.from({ length: val }, (_, i) => ({
      id: i,
      nome: `Período ${i + 1}`,
      valor: metas[i]?.valor || 0,
    }));
    setMetas(novos);
  };

  const onSave = async () => {
    try {
      await axios({
        method: 'post',
        url: `${process.env.REACT_APP_API_URL}admin/goal`,
        data: goal,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      })
      alert('Informações gravadas com sucesso.');
      setPage('goals');
    } catch (err) {
      console.log(err)
      alert('Erro. Revise e tente novamente!');
    } finally {
    }
  }

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <>
            <Step1
              goal={goal}
              setGoal={setGoal}
              value={periodos}
              onChange={handlePeriodoChange}
              onBack={handleBack}
              onNext={handleNext}
              activeStep={activeStep}
            />
          </>
        );
      case 1:
        return (
          <>
            <Step2
              goal={goal}
              setGoal={setGoal}
              onChange={handlePeriodoChange}
              onBack={handleBack}
              onNext={handleNext}
              activeStep={activeStep}
            />
          </>
        );
      case 2:
        return (
          <Step3
            goal={goal}
            setGoal={setGoal}
            sellers={sellers}
            setSellers={setSellers}
            absences={absences}
            setAbsences={setAbsences}
            onChange={handlePeriodoChange}
            onBack={handleBack}
            onNext={handleNext}
            activeStep={activeStep}
          />
        );
      case 3:
        return (
          <Step4
            goal={goal}
            setGoal={setGoal}
            sellers={sellers}
            absences={absences}
            onChange={handlePeriodoChange}
            onBack={handleBack}
            onNext={handleNext}
            activeStep={activeStep}
            onSave={onSave}
          />
        );
      case 4:
        return (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6">Resumo das metas</Typography>
            <pre>{JSON.stringify(goal, null, 2)}</pre>
            <Typography variant="body1" sx={{ mt: 2 }}>
              Quantidade de períodos: {periodos}
            </Typography>
            <Box sx={{ mt: 2 }}>
              {metas.map((meta) => (
                <Typography key={meta.id}>
                  {meta.nome}: R$ {meta.valor.toFixed(2)}
                </Typography>
              ))}
            </Box>
            <Button variant="outlined" sx={{ mt: 3 }} onClick={handleBack}>
              Voltar
            </Button>
          </Box>
        );
      default:
        return 'Etapa desconhecida';
    }
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 700, mx: 'auto', mt: 4 }}>
      <Stepper activeStep={activeStep} alternativeLabel>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Box>{getStepContent(activeStep)}</Box>

      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>Salvar metas</DialogTitle>
        <DialogContent>
          <Typography>Deseja realmente salvar as metas definidas?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleCloseDialog}>
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={errorMessage!==''} autoHideDuration={6000} onClose={() => {setErrorMessage('')}} >
        <Alert
          onClose={() => {setErrorMessage('')}}
          severity="error"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
